import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UpcomingSaleRequestForm } from '../../components/UpcomingSaleRequestForm';
import { UpcomingSaleSuccessModal } from '../../components/UpcomingSaleSuccessModal';
import { UpcomingSaleFailModal } from '../../components/UpcomingSaleFailModal';

// oxlint-disable-next-line cian-eslint-compat/no-restricted-paths
import { createUpcomingSaleRequest } from '../../../browser/services/upcomingSaleRequest';
import { getAnalyticsUser, getGaLabel } from '../../selectors/analytics';
import { IUpcomingSaleRequest } from '../../types/upcomingSaleRequest';
import { EUpcomingSaleTrackActions, trackUpcomingSaleRequest } from './tracking';
import { TDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';
import { TAgreement } from '@cian/newbuilding-flat-view-order-component';
import { saveLegalAgreements } from '../../utils/saveLegalAgreements/saveLegalAgreements';

export function UpcomingSaleRequestFormContainer() {
  const [isOpenSuccessModal, setIsOpenSuccessModal] = React.useState(false);
  const [isOpenFailModal, setIsOpenFailModal] = React.useState(false);
  const [isMarketingAgreementChecked, setMarketingAgreementChecked] = React.useState(false);
  const gaLabel = useSelector(getGaLabel);
  const user = useSelector(getAnalyticsUser);
  const dispatch: TDispatch = useDispatch();

  const { config, httpApi, logger } = useApplicationContext();

  const privacyPolicyUrl = config.get<string>('callback-widget.privacyPolicyUrl') || undefined;
  const marketingAgreementUrl = config.get<string>('callback-widget.marketingAgreementUrl') || undefined;
  const marketingAgreementVersion = config.get<number>('callback-widget.marketingAgreementVersion') || 1;
  const personalDataAgreementUrl = config.get<string>('callback-widget.personalDataAgreementUrl') || undefined;
  const personalDataAgreementVersion = config.get<number>('callback-widget.personalDataAgreementVersion') || 1;
  const isMarketingAgreementEnabled = config.get<boolean>('Newbuilding.Core.MarketingAgreement.Enabled') || false;

  const handleSuccessModalClose = React.useCallback(() => setIsOpenSuccessModal(false), []);
  const handleFailModalClose = React.useCallback(() => setIsOpenFailModal(false), []);

  const handleSubmit = React.useCallback(
    async (params: IUpcomingSaleRequest) => {
      const trackingParams = {
        gaLabel,
        phone: params.phone,
        user,
        name: params.userName,
      };

      try {
        trackUpcomingSaleRequest({ ...trackingParams, action: EUpcomingSaleTrackActions.Send });
        await dispatch(createUpcomingSaleRequest(params));

        setIsOpenSuccessModal(true);
        trackUpcomingSaleRequest({ ...trackingParams, action: EUpcomingSaleTrackActions.Success });

        const acceptedAgreements: TAgreement[] = ['personalDataDemand'];
        if (isMarketingAgreementChecked) {
          acceptedAgreements.push('marketing');
        }

        if (isMarketingAgreementEnabled) {
          saveLegalAgreements({
            httpApi,
            logger,
            acceptedAgreements,
            marketingAgreementVersion,
            personalDataAgreementVersion,
            source: 'newbuilding-card-desktop-frontend.upcoming-sale-request-form',
          });
        }

        return true;
      } catch {
        setIsOpenFailModal(true);
        trackUpcomingSaleRequest({ ...trackingParams, action: EUpcomingSaleTrackActions.Fail });

        return false;
      }
    },
    [
      dispatch,
      gaLabel,
      httpApi,
      isMarketingAgreementChecked,
      isMarketingAgreementEnabled,
      logger,
      marketingAgreementVersion,
      personalDataAgreementVersion,
      user,
    ],
  );

  return (
    <>
      <UpcomingSaleRequestForm
        onSubmit={handleSubmit}
        isMarketingAgreementEnabled={isMarketingAgreementEnabled}
        privacyPolicyUrl={privacyPolicyUrl}
        marketingAgreementUrl={marketingAgreementUrl}
        personalDataAgreementUrl={personalDataAgreementUrl}
        isMarketingAgreementChecked={isMarketingAgreementChecked}
        setMarketingAgreementChecked={setMarketingAgreementChecked}
      />
      <UpcomingSaleSuccessModal open={isOpenSuccessModal} onClose={handleSuccessModalClose} />
      <UpcomingSaleFailModal open={isOpenFailModal} onClose={handleFailModalClose} />
    </>
  );
}
